import {
  Checkbox,
  CheckboxCheckedFilled,
  Checkmark,
  ChevronDown,
  Close,
} from '@carbon/icons-react'
import type { KeyboardEvent, ReactNode } from 'react'
import type {
  ClassNamesConfig,
  GroupBase,
  InputProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  OptionProps,
  SingleValueProps,
  StylesConfig,
  ThemeConfig,
} from 'react-select'
import {
  type ClearIndicatorProps,
  components,
  type DropdownIndicatorProps,
} from 'react-select'
import { twMerge } from 'tailwind-merge'
import type { FieldBackground } from './FieldBorder'

export const GRAY_HOVER = '#c3c3c3'
export const PRIMARY_BLACK = '#161616'

export interface SelectOption {
  value: string
  label: string
  description?: string
  image?: ReactNode
}

/** `slim` is 32px high including the field border and used e.G. for ordering selection on desktop overview pages */
export type SelectVariant = 'regular' | 'slim'

export interface SelectGroup {
  label: string
  options: SelectOption[]
}

export function flattenOptions(
  options: (SelectOption | SelectGroup)[],
): SelectOption[] {
  return options.flatMap((option) => {
    if ('options' in option) {
      return flattenOptions(option.options)
    }
    return [option]
  })
}

/**
 * marks the image container inside a rendered value or placeholder, so the
 * slim variant can shrink it down to fit its 28px of content
 */
export const VALUE_IMAGE = 'select-value-image'

/**
 * shrinks {@link VALUE_IMAGE} containers to 20px, leaving room for the border.
 * spelled out literally so tailwind picks the utilities up
 */
const SLIM_VALUE_IMAGE =
  '[&_.select-value-image]:my-0 [&_.select-value-image]:h-5 [&_.select-value-image]:w-5'

/** 24px avatar containers in select/combobox value and option markup */
const AVATAR = 'h-6 w-6'

/**
 * react-select sets no font size on the input, the placeholder or a rendered
 * value, so declaring the type scale once on the control is enough: everything
 * below inherits it. The only exception is the multi value label, which
 * react-select hardcodes to 85% and which {@link comboboxStyles} resets.
 */
export function comboboxClassNames<
  Option extends { imageUrl?: string | null; renderIcon?: unknown },
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  variant: SelectVariant = 'regular',
): ClassNamesConfig<Option, IsMulti, Group> {
  const slim = variant === 'slim'
  const slimValue = `[&>div]:py-0 [&>div]:pl-0 ${SLIM_VALUE_IMAGE} [&_.select-value-subtitle]:hidden`
  return {
    control: () => 'body-compact-01',
    menu: () => 'body-compact-01',
    input: () => '[&>input:focus]:ring-0',
    multiValueRemove: (props) =>
      props.data.imageUrl || props.data.renderIcon
        ? 'opacity-0 absolute w-full h-full flex justify-center items-center hover:opacity-90 hover:z-10'
        : '',
    valueContainer: () => (slim ? 'min-h-7' : 'min-h-11'),
    singleValue: () => (slim ? slimValue : ''),
    placeholder: () => (slim ? slimValue : ''),
  }
}

export const selectClassNames = ({
  background,
  variant = 'regular',
}: {
  background: FieldBackground
  variant?: SelectVariant
}): ClassNamesConfig<SelectOption> => {
  const slim = variant === 'slim'
  // the inner wrappers of our own SingleValue/placeholder markup are spaced
  // for the default 48px field, which does not fit the slim one
  const slimValue = `[&>div]:py-0 [&>div]:pl-0 ${SLIM_VALUE_IMAGE}`

  return {
    input: () => '[&>input:focus]:ring-0',
    menu: () => 'body-compact-01',
    multiValueRemove: (props) =>
      props.data.image
        ? 'opacity-0 absolute w-full h-full flex justify-center items-center hover:opacity-90 hover:z-10'
        : '',
    control: () =>
      twMerge(
        background === 'white' ? 'bg-white' : 'bg-transparent',
        'body-compact-01',
      ),
    valueContainer: () => (slim ? 'min-h-7' : 'min-h-11'),
    singleValue: () => (slim ? slimValue : ''),
    placeholder: () => (slim ? slimValue : ''),
    multiValueLabel: () => (slim ? SLIM_VALUE_IMAGE : ''),
  }
}

/**
 * react-select renders its own styles through emotion, which injects them
 * without a cascade layer. Unlayered rules win over every tailwind utility
 * regardless of specificity, so anything react-select sets itself has to be
 * overridden here rather than through `classNames`.
 */
export function comboboxStyles<
  Option,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  options: {
    /** keeps the menu from collapsing while options are still loading */
    reserveMenuHeight?: boolean
    variant?: SelectVariant
  } = {},
): StylesConfig<Option, IsMulti, Group> {
  const slim = options.variant === 'slim'
  return {
    control: (base, props) => ({
      ...base,
      border: 'none',
      boxShadow: 'none',
      background: 'none',
      minHeight: slim ? '28px' : '44px',
      cursor: props.isDisabled ? 'not-allowed' : 'pointer',
      ':hover': {
        backgroundColor: props.isDisabled
          ? undefined
          : 'var(--color-field-hover-01)',
      },
    }),
    menu: (base, props) => ({
      ...base,
      borderRadius: '0',
      border: '1px solid var(--color-focus)',
      backgroundColor: 'var(--form-field-bg, var(--color-field-01))',
      boxShadow: 'var(--shadow-around)',
      maxHeight: props.maxMenuHeight,
      minHeight: options.reserveMenuHeight
        ? props.minMenuHeight
        : base.minHeight,
    }),
    menuList: (base) => ({
      ...base,
      paddingTop: 0,
      paddingBottom: 0,
    }),
    valueContainer: (base, props) => ({
      ...base,
      ...(props.isMulti ? { paddingLeft: '14px' } : {}),
      ...(slim ? { paddingTop: 0, paddingBottom: 0 } : {}),
    }),
    option: (base, props) => ({
      ...base,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      minHeight: slim ? '32px' : '48px',
      padding: slim ? '7px 16px' : '15px 16px',
      cursor: props.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: props.isSelected
        ? props.isFocused
          ? 'var(--color-layer-selected-hover-01)'
          : 'var(--color-layer-selected-01)'
        : props.isFocused
          ? 'var(--color-layer-hover-01)'
          : 'var(--form-field-bg, var(--color-field-01))',
      color: props.isSelected
        ? 'var(--color-text-primary)'
        : 'var(--color-text-secondary)',
      ':hover': {
        backgroundColor: props.isDisabled
          ? undefined
          : props.isSelected
            ? 'var(--color-layer-selected-hover-01)'
            : 'var(--color-layer-hover-01)',
      },
      ':active': {
        backgroundColor: props.isDisabled
          ? undefined
          : 'var(--color-layer-active-01)',
      },
      '& + .react-select__option::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '16px',
        right: '16px',
        height: '1px',
        backgroundColor: 'var(--color-border-subtle-01)',
      },
      '&:is(:hover, .react-select__option--is-focused, .react-select__option--is-selected)::before, &:is(:hover, .react-select__option--is-focused, .react-select__option--is-selected) + .react-select__option::before':
        {
          display: 'none',
        },
    }),
    input: (base) => ({
      ...base,
      outline: 'none',
      boxShadow: 'none',
      ...(slim ? { margin: 0, paddingTop: 0, paddingBottom: 0 } : {}),
    }),
    dropdownIndicator: (base) => ({
      ...base,
      ...(slim ? { padding: '4px' } : {}),
      cursor: 'pointer',
    }),
    clearIndicator: (base) => ({
      ...base,
      ...(slim ? { padding: '4px' } : {}),
      cursor: 'pointer',
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none',
      '.react-select__clear-indicator ~ &': {
        display: 'block',
      },
    }),
    // react-select hardcodes 85% here, the only font size it sets on its own
    multiValueLabel: (base) => ({ ...base, fontSize: 'inherit' }),
  }
}

const baseSelectStyles = comboboxStyles<SelectOption>()

export const selectStyles: StylesConfig<SelectOption> = {
  ...baseSelectStyles,
  control: (base, props) => ({
    ...baseSelectStyles.control?.(base, props),
    height: '100%',
    backgroundColor: undefined,
  }),
  multiValue: (base, props) =>
    props.data.image
      ? {
          ...base,
          position: 'relative',
          marginRight: '-0.75rem',
          borderRadius: '9999px',
          overflow: 'hidden',
          marginTop: '0.57rem',
          marginBottom: '0.57rem',
        }
      : base,
}

/**
 * compact variant of {@link selectStyles}: 28px of content, which adds up to
 * 32px including the field border
 */
export const slimSelectStyles: StylesConfig<SelectOption> = {
  ...selectStyles,
  option: comboboxStyles<SelectOption>({ variant: 'slim' }).option,
  control: (base, props) => ({
    ...selectStyles.control?.(base, props),
    minHeight: '1.75rem',
  }),
  valueContainer: (base, props) => ({
    ...selectStyles.valueContainer?.(base, props),
    paddingTop: 0,
    paddingBottom: 0,
  }),
  input: (base, props) => ({
    ...selectStyles.input?.(base, props),
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  multiValue: (base, props) =>
    props.data.image
      ? {
          ...selectStyles.multiValue?.(base, props),
          marginRight: '-0.5rem',
          marginTop: '0.125rem',
          marginBottom: '0.125rem',
        }
      : base,
  // the default label padding would push the shrunk image past the 28px again
  multiValueLabel: (base, props) => ({
    ...selectStyles.multiValueLabel?.(base, props),
    ...(props.data.image ? { padding: 0, paddingLeft: 0 } : {}),
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0.25rem',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0.25rem',
  }),
}

export const comboboxTheme: ThemeConfig = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    dangerLight: GRAY_HOVER,
  },
})

export function ComboboxOption<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: OptionProps<Option, IsMulti, Group>) {
  return (
    <components.Option {...props}>
      {props.isMulti ? (
        <div className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={twMerge(
              'flex size-4 shrink-0 items-center text-icon-primary',
              props.isDisabled && 'text-icon-disabled',
            )}
          >
            {props.isSelected ? (
              <CheckboxCheckedFilled size={16} />
            ) : (
              <Checkbox size={16} />
            )}
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {props.children}
          </div>
        </div>
      ) : (
        props.children
      )}
    </components.Option>
  )
}

export const selectComponents = {
  DropdownIndicator(props: DropdownIndicatorProps<SelectOption>) {
    return (
      <components.DropdownIndicator {...props}>
        <ChevronDown
          className={
            props.isDisabled ? 'text-gray-disabled' : 'text-text-primary'
          }
        />
      </components.DropdownIndicator>
    )
  },
  Option(props: OptionProps<SelectOption>) {
    return (
      <ComboboxOption {...props}>
        <div className="flex items-center gap-4">
          {props.data.image && (
            <div
              className={`relative flex ${AVATAR} items-center justify-center overflow-hidden rounded-full`}
            >
              {props.data.image}
            </div>
          )}
          <span className="min-w-0 flex-1 truncate">{props.data.label}</span>
          {!props.isMulti && props.isSelected && (
            <Checkmark className="ml-auto" />
          )}
        </div>
        {props.data.description && (
          <span className="text-text-secondary">{props.data.description}</span>
        )}
      </ComboboxOption>
    )
  },
  SingleValue(props: SingleValueProps<SelectOption>) {
    return (
      <components.SingleValue {...props}>
        <div className="flex items-center gap-2 py-1 pl-2">
          {props.data.image && (
            <div
              className={`${VALUE_IMAGE} relative my-1 ${AVATAR} overflow-hidden rounded-full`}
            >
              {props.data.image}
            </div>
          )}
          <span
            className={twMerge(
              props.isDisabled ? 'text-gray-disabled' : 'text-text-primary',
              'truncate',
            )}
          >
            {props.children}
          </span>
        </div>
      </components.SingleValue>
    )
  },
  ClearIndicator(props: ClearIndicatorProps<SelectOption>) {
    return (
      <components.ClearIndicator {...props}>
        <Close className="text-text-primary" />
      </components.ClearIndicator>
    )
  },
  MultiValueLabel(props: MultiValueGenericProps<SelectOption>) {
    return (
      <components.MultiValueLabel {...props}>
        {props.data.image ? (
          <div
            className={`${VALUE_IMAGE} relative ${AVATAR} overflow-hidden rounded-full`}
          >
            {props.data.image}
          </div>
        ) : (
          <span>{props.data.label}</span>
        )}
      </components.MultiValueLabel>
    )
  },
  MultiValueRemove(props: MultiValueRemoveProps<SelectOption>) {
    return (
      <components.MultiValueRemove {...props}>
        <Close className="text-text-primary" />
      </components.MultiValueRemove>
    )
  },
  Input(props: InputProps<SelectOption>) {
    return <components.Input {...props} aria-activedescendant={undefined} />
  },
}

export function controlFocus(
  input: HTMLInputElement | null | undefined,
  event: KeyboardEvent<HTMLDivElement>,
) {
  if (!input) return
  if (event.key === 'Tab') {
    // so react-select within radix dialog breaks focus order
    // so we are going to implement our own focus management here.

    // get all the form elements in the current form
    const formElements = Array.from(input.form?.elements ?? [])
    // find our own element
    const currentIndex = formElements.indexOf(input)
    // not there? forget this. We're probably not in a form.
    if (currentIndex === -1) return

    // find the next element that is
    // * a HTMLElement (they all are, this is for type safety)
    // * visible (as in not a hidden input)
    const nextElement = (
      event.shiftKey
        ? // shift+tab -> search in reverse order before our current element
          formElements.slice(0, currentIndex).reverse()
        : // regular tab -> search after our current element
          formElements.slice(currentIndex + 1)
    ).find(
      (element): element is HTMLElement =>
        element instanceof HTMLElement && element.checkVisibility(),
    )

    if (nextElement) {
      // found one? focus it and prevent the focus guard from messing this up
      event.stopPropagation()
      event.preventDefault()
      nextElement.focus()
    }
  }
}
